'use client';

import React, { memo } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const team = [
  {
    name: 'Rahul Sharma',
    role: 'Production Manager',
    img: '/images/employee-1.jpg',
  },
  {
    name: 'Priya Verma',
    role: 'Quality Control Lead',
    img: '/images/employee-3.jpg',
  },
  {
    name: 'Amit Mehta',
    role: 'Packaging Operations Head',
    img: '/images/employee-6.jpg',
  },
];

function AboutTeam() {
  return (
    <section aria-labelledby="team-heading" className="w-full bg-[#F1F3F6] py-14 md:py-16 lg:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 md:px-8">
        {/* Heading + subcopy */}
        <header className="text-center mb-8 md:mb-10">
          <h2
            id="team-heading"
            className="text-slate-900 font-extrabold tracking-[-0.01em]
                       text-[clamp(30px,4.5vw,40px)] leading-[1.15]"
          >
            Meet Our Amazing Team
          </h2>
          <p
            className="mx-auto mt-3 max-w-[820px]
                       text-slate-700 text-[clamp(16px,2.2vw,17px)]
                       leading-[clamp(24px,3.1vw,26px)]"
          >
            Behind every label is a team of problem-solvers, engineers, print technicians, and
            design thinkers. We are a growing, skilled, and passionate crew driven by results and
            craft.
          </p>
        </header>

        {/* Team grid */}
        <div
          className="
            grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4
            gap-x-6 sm:gap-x-8 gap-y-10
          "
          role="list"
          aria-label="Team members"
        >
          {team.map((m, i) => (
            <article role="listitem" key={`${m.name}-${i}`} className="flex flex-col">
              {/* Image tile matches square with rounded-12, subtle shadow */}
              <div className="relative w-full aspect-[1/1] overflow-hidden rounded-[12px] shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
                <Image
                  src={m.img}
                  alt={m.name}
                  fill
                  sizes="(min-width:1024px) 280px, (min-width:640px) 30vw, 45vw"
                  className="object-cover"
                  priority={false}
                />
              </div>

              {/* Name + role left-aligned under image */}
              <div className="mt-3">
                <h3 className="text-slate-900 text-[16px] md:text-[17px] font-semibold leading-[1.35]">
                  {m.name}
                </h3>
                <p className="mt-1 text-slate-600 text-[14px] leading-[20px]">{m.role}</p>
              </div>
            </article>
          ))}
        </div>

        {/* Join our team callout card */}
        <aside
          className="
            mt-10 md:mt-12
            rounded-[14px] border border-slate-200 bg-white
            shadow-[0_1px_2px_rgba(15,23,42,0.04)]
            px-5 sm:px-6 md:px-8 py-6 md:py-7
          "
          aria-labelledby="join-team-heading"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start">
            <div className="md:col-span-4">
              <h3
                id="join-team-heading"
                className="text-slate-900 font-semibold
                           text-[clamp(18px,2.4vw,22px)] leading-[1.25]"
              >
                Join our team
              </h3>
            </div>

            <div className="md:col-span-6">
              <p className="text-slate-700 text-[15px] md:text-[16px] leading-[22px] md:leading-[24px]">
                We are building a skilled manufacturing team focused on print consistency, production efficiency and packaging excellence. Join us if you are passionate about industrial printing, operations and quality focused manufacturing.
              </p>
            </div>

            <div className="md:col-span-2 md:justify-self-end">
              <Link
                href="/careers"
                className="
                  inline-flex items-center gap-2 rounded-[12px] border border-slate-300
                  px-4 h-10 text-[15px] font-semibold text-slate-800
                  hover:border-slate-400 hover:bg-slate-50
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-800/60 focus-visible:ring-offset-2
                  transition-colors
                "
              >
                Explore Careers
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M7 17l7-7M10 7h8v8"
                    stroke="#0F172A"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default memo(AboutTeam);
